import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PackageCheck } from 'lucide-react'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import {
  AvatarGroup,
  PriorityChip,
  TypeGlyph,
} from '@/components/common/badges'
import { IssueLink } from '@/components/common/issue-link'
import { DAY_MS, localToday, toDate } from '@/lib/local-date'
import { sortedQuarters, type Quarter } from '@/lib/quarter'
import { cn } from '@/lib/utils'
import { liveReleasedAt, type WorkIssue } from '@/types/github'

export type Zoom = 'quarter' | 'month' | 'week'

const PX_PER_DAY: Record<Exclude<Zoom, 'quarter'>, number> = { month: 10, week: 40 }
const ROW_H = 38
const BAND_PAD = 34

type Case = 'released' | 'target' | 'quarterOnly'

type Item = {
  issue: WorkIssue
  kind: Case
  start: Date | null
  end: Date | null
  quarter: string
}

/**
 * Data-honest classification — exactly three cases:
 * A released_at set → history bar; B target_date set → committed bar;
 * C quarter only → centered dashed card. Anything undatable is reported
 * by the caller, never invented.
 */
function classify(issue: WorkIssue, quarters: Quarter[]): Item | null {
  const startDate = toDate(issue.promotedAt ?? issue.createdAt)
  const quarterOf = (d: Date | null): string | null => {
    if (!d) return null
    return quarters.find((q) => q.start <= d && d < q.end)?.name ?? null
  }

  const released = liveReleasedAt(issue)
  if (released) {
    const end = toDate(released)
    const quarter = issue.quarter ?? quarterOf(end)
    return quarter ? { issue, kind: 'released', start: startDate, end, quarter } : null
  }
  if (issue.targetDate) {
    const end = toDate(issue.targetDate)
    const quarter = issue.quarter ?? quarterOf(end)
    return quarter ? { issue, kind: 'target', start: startDate, end, quarter } : null
  }
  if (issue.quarter) {
    return { issue, kind: 'quarterOnly', start: null, end: null, quarter: issue.quarter }
  }
  return null
}

function BarHover({ item, children }: { item: Item; children: React.ReactNode }) {
  const { issue } = item
  return (
    <HoverCard openDelay={250}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-72 text-xs" side="top">
        <div className="flex items-center gap-2">
          <span className="text-faint-foreground font-mono text-[11px]">#{issue.number}</span>
          <TypeGlyph typeName={issue.typeName} />
          <PriorityChip priority={issue.priority} />
        </div>
        <p className="mt-1 font-medium">{issue.title}</p>
        <p className="text-muted-foreground mt-1 font-mono text-[11px]">
          {issue.client && `${issue.client} · `}
          {issue.product}
        </p>
        {(item.start || item.end) && (
          <p className="text-muted-foreground mt-1 font-mono text-[11px]">
            {item.start?.toISOString().slice(0, 10)} → {item.end?.toISOString().slice(0, 10)}
            {item.kind === 'target' && ' (target)'}
          </p>
        )}
        <div className="mt-2">
          <AvatarGroup users={issue.assignees} />
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

export function Timeline({ issues, quarterOptions, zoom }: {
  issues: WorkIssue[]
  quarterOptions: string[]
  zoom: Zoom
}) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [frameWidth, setFrameWidth] = useState(1200)

  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return
    const update = () => setFrameWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const quarters = useMemo(() => sortedQuarters(quarterOptions), [quarterOptions])

  const { items, bands, horizonStart, totalDays } = useMemo(() => {
    const known = new Set(quarters.map((q) => q.name))
    // Items in a quarter we can't place on the axis (renamed/non-canonical
    // option) count as skipped rather than silently disappearing.
    const classified = issues
      .map((i) => classify(i, quarters))
      .filter((i): i is Item => i !== null && known.has(i.quarter))
    const bandsMap = new Map<string, Item[]>()
    for (const q of quarters) bandsMap.set(q.name, [])
    for (const item of classified) {
      bandsMap.get(item.quarter)!.push(item)
    }
    const first = quarters[0]
    const last = quarters[quarters.length - 1]
    const start = first?.start ?? localToday()
    const days = first && last ? Math.round((last.end.getTime() - start.getTime()) / DAY_MS) : 0
    return { items: classified, bands: bandsMap, horizonStart: start, totalDays: days }
  }, [issues, quarters])

  if (quarters.length === 0) {
    return (
      <p className="text-muted-foreground grid h-full place-items-center text-sm">
        No Quarter options defined.
      </p>
    )
  }

  const skipped = issues.length - items.length

  if (items.length === 0) {
    return (
      <div className="text-muted-foreground m-auto flex h-full flex-col items-center justify-center gap-1 p-10 text-center text-sm">
        <p>Nothing on the roadmap.</p>
        <p className="text-faint-foreground text-xs">
          Features appear here once they have a Released/Target date or a Quarter
          {skipped > 0 ? ` — ${skipped} feature${skipped === 1 ? '' : 's'} lack all three.` : '.'}
        </p>
      </div>
    )
  }

  const pxPerDay = zoom === 'quarter' ? Math.max((frameWidth - 2) / totalDays, 0.5) : PX_PER_DAY[zoom]
  const x = (d: Date) => ((d.getTime() - horizonStart.getTime()) / DAY_MS) * pxPerDay
  const canvasWidth = totalDays * pxPerDay
  const today = localToday()

  let cursor = 34 // axis height
  const bandTops = new Map<string, { top: number; height: number }>()
  for (const q of quarters) {
    const count = bands.get(q.name)?.length ?? 0
    // Empty quarters collapse to a sliver instead of a labelled void.
    const height = count === 0 ? 10 : BAND_PAD + count * ROW_H + 8
    bandTops.set(q.name, { top: cursor, height })
    cursor += height
  }
  const canvasHeight = Math.max(cursor, 360) // keep the Today line visible

  return (
    <div className="flex h-full flex-col gap-1">
      {skipped > 0 && (
        <p className="text-faint-foreground px-4 text-[11px]">
          {skipped} feature{skipped === 1 ? '' : 's'} without Released/Target/Quarter not shown —
          no dates are invented.
        </p>
      )}
      <div
        ref={frameRef}
        className="border-border bg-card relative m-4 mt-0 min-h-0 flex-1 overflow-auto rounded-xl border"
      >
        <div className="relative" style={{ width: canvasWidth, height: canvasHeight }}>
          {/* axis */}
          <div className="bg-card border-border sticky top-0 z-20 flex h-[34px] border-b">
            {quarters.map((q) => (
              <div
                key={q.name}
                className="text-muted-foreground border-border flex items-center border-r px-2.5 text-[11.5px] font-semibold"
                style={{ width: ((q.end.getTime() - q.start.getTime()) / DAY_MS) * pxPerDay }}
              >
                {q.name}
              </div>
            ))}
          </div>

          {/* quarter gridlines */}
          {quarters.map((q) => (
            <div
              key={q.name}
              className="bg-border absolute top-0 bottom-0 w-px opacity-60"
              style={{ left: x(q.start) }}
              aria-hidden
            />
          ))}

          {/* today */}
          {x(today) >= 0 && x(today) <= canvasWidth && (
            <div
              className="absolute top-0 bottom-0 z-10 border-l border-dashed border-[color-mix(in_oklch,var(--primary)_45%,transparent)]"
              style={{ left: x(today) }}
            >
              <span className="bg-primary/85 text-primary-foreground sticky top-[38px] ml-0.5 inline-block rounded-sm px-1 py-px text-[9.5px] font-semibold">
                Today
              </span>
            </div>
          )}

          {/* bands */}
          {quarters.map((q) => {
            const layout = bandTops.get(q.name)!
            const bandItems = bands.get(q.name) ?? []
            const bandWidth = ((q.end.getTime() - q.start.getTime()) / DAY_MS) * pxPerDay
            return (
              <div key={q.name}>
                <div
                  className="border-border absolute right-0 left-0 border-b opacity-50"
                  style={{ top: layout.top + layout.height }}
                  aria-hidden
                />
                {bandItems.length > 0 && (
                  <span
                    className="text-muted-foreground bg-card/85 sticky z-10 m-2 inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-semibold backdrop-blur-sm"
                    style={{ position: 'absolute', top: layout.top + 4, left: 8 }}
                  >
                    {q.name}
                    <span className="text-faint-foreground font-normal">{bandItems.length}</span>
                  </span>
                )}
                {bandItems.map((item, row) => {
                  const top = layout.top + BAND_PAD + row * ROW_H
                  if (item.kind === 'quarterOnly') {
                    // Centered in the quarter band — pinning to the start
                    // would misread as a commitment.
                    const left = x(q.start) + bandWidth / 2 - 125
                    return (
                      <BarHover key={item.issue.id} item={item}>
                        <IssueLink
                          number={item.issue.number}
                          className="border-border-strong bg-card hover:border-solid absolute z-[5] block w-[250px] rounded-lg border border-dashed px-2.5 py-1.5 text-left"
                          style={{ top, left: Math.max(left, 4) }}
                        >
                          <span className="flex items-center gap-1.5 text-xs">
                            <span className="text-faint-foreground font-mono text-[10px]">
                              #{item.issue.number}
                            </span>
                            <span className="truncate font-medium">{item.issue.title}</span>
                          </span>
                          <span className="text-faint-foreground mt-0.5 flex items-center gap-1.5 font-mono text-[10.5px]">
                            <PriorityChip priority={item.issue.priority} />
                            {q.name} — no committed dates
                          </span>
                        </IssueLink>
                      </BarHover>
                    )
                  }

                  const start = item.start!
                  const end = item.end!
                  const left = Math.max(x(start), 0)
                  const width = Math.max(x(end) - left, 24)
                  const released = item.kind === 'released'
                  const todayX = x(today)
                  const caretVisible =
                    item.kind === 'target' && todayX > left && todayX < left + width
                  return (
                    <BarHover key={item.issue.id} item={item}>
                      <IssueLink
                        number={item.issue.number}
                        className={cn(
                          'absolute z-[5] flex h-[30px] items-center gap-1.5 overflow-hidden rounded-md border-l-[3px] px-2 text-left text-[11.5px] whitespace-nowrap shadow-[var(--shadow-card)] hover:z-10 hover:shadow-[var(--shadow-card-hover)]',
                          released
                            ? 'border-l-[var(--st-done)] bg-[color-mix(in_oklch,var(--st-done)_9%,var(--card))]'
                            : 'border-l-border-strong bg-card',
                        )}
                        style={{ top: top + 4, left, width }}
                      >
                        <span className="text-faint-foreground font-mono text-[10px]">
                          #{item.issue.number}
                        </span>
                        <span className="flex-1 truncate font-medium">{item.issue.title}</span>
                        {released ? (
                          <PackageCheck className="size-3.5 shrink-0 text-[var(--st-done)]" />
                        ) : (
                          <PriorityChip priority={item.issue.priority} />
                        )}
                        {caretVisible && (
                          <span
                            className="absolute top-0 h-0 w-0 border-x-4 border-t-[5px] border-x-transparent border-t-[var(--primary)]"
                            style={{ left: todayX - left - 4 }}
                            aria-hidden
                          />
                        )}
                      </IssueLink>
                    </BarHover>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
